import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Send, PackageCheck, Lock, Clock, X, Loader2, Building2 } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import { api } from "../../api/client";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const STATUS_TONE = {
    draft: "bg-zinc-100 text-zinc-700",
    sent: "bg-amber-100 text-amber-800",
    received: "bg-emerald-100 text-emerald-700",
    closed: "bg-zinc-200 text-zinc-700",
};

const Content = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [po, setPo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [receiveOpen, setReceiveOpen] = useState(false);
    const [receipts, setReceipts] = useState({}); // {product_id: {received_qty, batch_no, expiry_date, unit_cost}}
    const [notes, setNotes] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/purchase-orders/${id}`);
            setPo(data);
        } catch {
            toast.error("PO not found");
            navigate("/admin/purchase-orders");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const send = async () => {
        if (!window.confirm("Send PO to vendor? Status will change to 'sent'.")) return;
        setBusy(true);
        try {
            await api.post(`/admin/purchase-orders/${id}/send`);
            toast.success("PO sent");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || "Could not send");
        } finally {
            setBusy(false);
        }
    };

    const close = async () => {
        if (!window.confirm("Close this PO? No more receipts can be recorded.")) return;
        setBusy(true);
        try {
            await api.post(`/admin/purchase-orders/${id}/close`);
            toast.success("PO closed");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || "Could not close");
        } finally {
            setBusy(false);
        }
    };

    const openReceive = () => {
        setReceipts({});
        setNotes("");
        // pre-fill with remaining qty
        const next = {};
        (po?.items || []).forEach((it) => {
            const remaining = Math.max(0, it.qty - (it.received_qty || 0));
            next[it.product_id] = { received_qty: remaining, batch_no: "", expiry_date: "", unit_cost: it.unit_cost };
        });
        setReceipts(next);
        setReceiveOpen(true);
    };

    const submitReceive = async () => {
        const items = (po?.items || [])
            .map((it) => {
                const r = receipts[it.product_id];
                if (!r || !(r.received_qty > 0)) return null;
                return {
                    product_id: it.product_id,
                    received_qty: Number(r.received_qty),
                    batch_no: r.batch_no || null,
                    expiry_date: r.expiry_date || null,
                    unit_cost: r.unit_cost != null ? Number(r.unit_cost) : null,
                };
            })
            .filter(Boolean);
        if (items.length === 0) { toast.error("Enter received qty for at least one item"); return; }
        setBusy(true);
        try {
            await api.post(`/admin/purchase-orders/${id}/receive`, { items, notes: notes || null });
            toast.success("Receipt recorded · stock updated");
            setReceiveOpen(false);
            await load();
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Could not record receipt");
        } finally {
            setBusy(false);
        }
    };

    if (loading || !po) return <div className="h-[60vh] skeleton rounded-2xl" />;

    return (
        <div className="space-y-5" data-testid="admin-po-detail">
            <div className="flex items-center justify-between">
                <Link to="/admin/purchase-orders" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-brand-700">
                    <ArrowLeft className="h-4 w-4" /> All purchase orders
                </Link>
                <span className={`rounded-full px-3 py-1 text-[10px] font-semibold uppercase ${STATUS_TONE[po.status]}`}>{po.status}</span>
            </div>

            <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
                <div className="space-y-5">
                    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                            <div>
                                <p className="label-uppercase">Purchase order</p>
                                <h2 className="mt-1 text-xl font-semibold tracking-tight text-slate-900" data-testid="admin-po-no">{po.po_no}</h2>
                                <p className="text-xs text-slate-500">Created {new Date(po.created_at).toLocaleString()}</p>
                                {po.expected_delivery_date && <p className="text-xs text-slate-500">Expected delivery: {po.expected_delivery_date}</p>}
                            </div>
                            <p className="text-2xl font-semibold tabular-nums">{inr(po.total)}</p>
                        </div>

                        <ul className="mt-5 divide-y divide-slate-100">
                            {(po.items || []).map((it) => {
                                const fully = (it.received_qty || 0) >= it.qty;
                                return (
                                    <li key={it.product_id} className="flex items-center justify-between gap-3 py-3 text-sm">
                                        <div className="min-w-0 flex-1">
                                            <p className="font-medium text-slate-900">{it.name}</p>
                                            <p className="text-xs text-slate-500">{it.brand} · {it.pack_size} · {it.qty} × {inr(it.unit_cost)}</p>
                                            <p className="text-[11px] text-slate-500">
                                                Received: <strong className={fully ? "text-emerald-700" : "text-amber-700"}>{it.received_qty || 0} / {it.qty}</strong>
                                            </p>
                                        </div>
                                        <p className="text-sm font-medium tabular-nums">{inr(it.subtotal)}</p>
                                    </li>
                                );
                            })}
                        </ul>
                        {po.notes && (
                            <p className="mt-4 rounded-md bg-zinc-50 p-3 text-xs text-slate-700"><strong>Notes:</strong> {po.notes}</p>
                        )}
                    </section>

                    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft" data-testid="admin-po-history">
                        <p className="label-uppercase">History</p>
                        <ol className="mt-3 space-y-2.5">
                            {(po.history || []).map((h, i) => (
                                <li key={i} className="flex items-start gap-2.5 text-xs">
                                    <Clock className="mt-0.5 h-3 w-3 shrink-0 text-slate-400" />
                                    <div>
                                        <p className="font-medium capitalize text-slate-900">{h.event.replace("_", " ")}</p>
                                        {h.note && <p className="text-slate-500">{h.note}</p>}
                                        <p className="text-[10px] uppercase tracking-wider text-slate-400">{new Date(h.at).toLocaleString()}</p>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </section>
                </div>

                <aside className="space-y-5">
                    {po.vendor && (
                        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft">
                            <p className="label-uppercase">Vendor</p>
                            <Building2 className="mt-3 h-4 w-4 text-slate-400" />
                            <p className="mt-1 text-sm font-medium">{po.vendor.name}</p>
                            <p className="text-xs text-slate-500">{po.vendor.gstin || "—"}</p>
                            <p className="text-xs text-slate-500">{po.vendor.contact_person} · {po.vendor.phone}</p>
                            <p className="text-xs text-slate-500">{po.vendor.email}</p>
                            <p className="text-xs text-slate-500">Net {po.vendor.payment_terms_days}</p>
                        </div>
                    )}

                    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft" data-testid="admin-po-actions">
                        <p className="label-uppercase">Actions</p>
                        <div className="mt-3 flex flex-col gap-2">
                            {po.status === "draft" && (
                                <button onClick={send} disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-lg bg-amber-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50" data-testid="admin-po-send">
                                    <Send className="h-4 w-4" /> Send to vendor
                                </button>
                            )}
                            {(po.status === "sent" || po.status === "received") && (
                                <button onClick={openReceive} disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50" data-testid="admin-po-receive">
                                    <PackageCheck className="h-4 w-4" /> Record receipt
                                </button>
                            )}
                            {(po.status === "sent" || po.status === "received") && (
                                <button onClick={close} disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-lg border border-zinc-200 px-4 py-2 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50" data-testid="admin-po-close">
                                    <Lock className="h-3.5 w-3.5" /> Close PO
                                </button>
                            )}
                        </div>
                    </div>
                </aside>
            </div>

            {receiveOpen && (
                <div className="fixed inset-0 z-[70] grid place-items-center bg-slate-900/40 p-4 backdrop-blur-sm">
                    <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" data-testid="admin-po-receive-modal">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="label-uppercase">Record receipt</p>
                                <h2 className="text-lg font-semibold">{po.po_no}</h2>
                            </div>
                            <button onClick={() => setReceiveOpen(false)} className="rounded-full p-1.5 text-zinc-400 hover:bg-zinc-100"><X className="h-4 w-4" /></button>
                        </div>
                        <p className="mt-2 text-xs text-slate-500">Enter received quantities + batch info. Stock will be increased and a batch record created.</p>

                        <div className="mt-4 overflow-x-auto">
                            <table className="w-full text-xs" data-testid="admin-po-receive-table">
                                <thead className="bg-zinc-50 text-left text-[10px] uppercase tracking-wider text-zinc-500">
                                    <tr>
                                        <th className="px-3 py-2">Product</th>
                                        <th className="px-3 py-2">Ordered</th>
                                        <th className="px-3 py-2">Already received</th>
                                        <th className="px-3 py-2">Receive now</th>
                                        <th className="px-3 py-2">Batch #</th>
                                        <th className="px-3 py-2">Expiry</th>
                                        <th className="px-3 py-2">Unit cost</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(po.items || []).map((it) => {
                                        const r = receipts[it.product_id] || {};
                                        return (
                                            <tr key={it.product_id} className="border-t border-zinc-100">
                                                <td className="px-3 py-2">
                                                    <p className="font-medium">{it.name}</p>
                                                    <p className="text-[10px] text-slate-500">{it.brand} · {it.pack_size}</p>
                                                </td>
                                                <td className="px-3 py-2">{it.qty}</td>
                                                <td className="px-3 py-2">{it.received_qty || 0}</td>
                                                <td className="px-3 py-2"><input type="number" min="0" max={it.qty} value={r.received_qty ?? ""} onChange={(e) => setReceipts({ ...receipts, [it.product_id]: { ...r, received_qty: e.target.value === "" ? "" : Number(e.target.value) } })} className="w-20 rounded border border-zinc-200 px-2 py-1" data-testid={`admin-po-receive-qty-${it.product_id}`} /></td>
                                                <td className="px-3 py-2"><input value={r.batch_no || ""} onChange={(e) => setReceipts({ ...receipts, [it.product_id]: { ...r, batch_no: e.target.value } })} placeholder="B/12345" className="w-28 rounded border border-zinc-200 px-2 py-1" data-testid={`admin-po-receive-batch-${it.product_id}`} /></td>
                                                <td className="px-3 py-2"><input type="date" value={r.expiry_date || ""} onChange={(e) => setReceipts({ ...receipts, [it.product_id]: { ...r, expiry_date: e.target.value } })} className="rounded border border-zinc-200 px-2 py-1" data-testid={`admin-po-receive-expiry-${it.product_id}`} /></td>
                                                <td className="px-3 py-2"><input type="number" step="0.01" min="0" value={r.unit_cost ?? ""} onChange={(e) => setReceipts({ ...receipts, [it.product_id]: { ...r, unit_cost: e.target.value === "" ? "" : Number(e.target.value) } })} className="w-24 rounded border border-zinc-200 px-2 py-1" data-testid={`admin-po-receive-cost-${it.product_id}`} /></td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <label className="mt-4 block text-sm">
                            <span className="text-xs text-zinc-500">Notes</span>
                            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 w-full rounded-md border border-zinc-200 p-2 text-sm" data-testid="admin-po-receive-notes" />
                        </label>

                        <div className="mt-5 flex justify-end gap-2">
                            <button onClick={() => setReceiveOpen(false)} className="rounded-md border border-zinc-200 px-4 py-2 text-sm">Cancel</button>
                            <button onClick={submitReceive} disabled={busy} className="inline-flex items-center gap-1 rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800 disabled:opacity-50" data-testid="admin-po-receive-submit">
                                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <PackageCheck className="h-3 w-3" />}
                                {busy ? "Recording…" : "Record receipt"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const AdminPurchaseOrderDetail = () => (
    <AdminAuthGuard>
        <AdminLayout title="Purchase order" subtitle="Detail · send · receive">
            <Content />
        </AdminLayout>
    </AdminAuthGuard>
);

export default AdminPurchaseOrderDetail;
